import { useEffect, useMemo, useState } from 'react';
import type {
  InboxBlock,
  InboxDuplicateRef,
  InboxEquipmentRow,
  InboxLaborRow,
  SettingsPco,
} from '@coffman-tm/shared';
import { api, ApiCallError } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';
import {
  invalidatePcoSubmissions,
  submissionErrorMessage,
  usePcoSubmissions,
} from '../../lib/useSubmissions';
import {
  formatDateShort,
  formatMoneyCents,
  formatPcoNumber,
  formatSubmissionLabel,
  formatSubmissionShort,
  gcName,
  weekEndingFor,
} from '../../lib/format';
import {
  equipmentSubtotalCents,
  laborSubtotalCents,
  materialsSubtotalCents,
} from './blockSubtotals';
import { InboxLaborTable, initialLaborEdits, type LaborEdit } from './InboxLaborTable';
import { rateForDaypart } from './laborEdits';
import { pickInitialSubmissionChoice } from './submissionPreselect';
import { InboxReassignModal, type ReassignTarget } from './InboxReassignModal';
import {
  InboxEquipmentTable,
  initialEquipmentEdits,
  type EquipmentEdit,
} from './InboxEquipmentTable';
import { InboxMaterialsList, billableMaterialRows } from './InboxMaterialsList';
import { PcoAssignmentDropdown } from './PcoAssignmentDropdown';
import { AlertTriangle, FileText } from 'lucide-react';
import { Dropdown } from '../design';
import { HelpTip } from '../HelpTip';
import { ConfirmModal } from '../ConfirmModal';
import { useToast } from '../ToastProvider';
import { AddEmployeeModal } from '../AddEmployeeModal';
import { AddEquipmentModal } from '../AddEquipmentModal';
import { InboxAddLineModal } from './InboxAddLineModal';
import { TransferProjectModal } from './TransferProjectModal';
import { StartNextSubmissionDialog } from '../StartNextSubmissionDialog';

/**
 * One reviewable activity-code block. Owns the local labor/equipment edit
 * state + the chosen pco_id; on save, posts a bundled payload to
 * /api/inbox/blocks/:id/save and tells the parent to refetch.
 */

/** Menu-entry sentinel for the submission picker — opens the Start Next
 *  dialog. Never stored as `submissionChoice`. */
const START_NEXT = '__start_next__';

/**
 * Add-line buttons are the LEAST-used control on the card and were its heaviest
 * element (solid `btn-dark`). Demoted to the design system's secondary button;
 * the size overrides match the compact scale the section header sits on.
 */
const addLineButtonStyle: React.CSSProperties = { padding: '4px 10px', fontSize: 12 };

/**
 * One header row per data section — Labor / Equipment / Materials.
 *
 * Title-case bold + line count on the left with its "?", subtotal and the add
 * button on the right. Shared rather than repeated three times so the sections
 * cannot drift apart, which is the entire point of the hierarchy: the reader
 * should be able to learn the shape once.
 *
 * Note the deliberate split from the Add-PCO modals, which use gray eyebrow
 * section titles — these head data tables with money in them, those head form
 * fields. Flagged for a later unification pass rather than guessed at here.
 */
function SectionHeader({
  title,
  count,
  subtotalCents,
  help,
  action,
}: {
  title: string;
  count: number;
  /** `null` renders no subtotal at all — see the Materials call site. */
  subtotalCents: number | null;
  help: React.ReactNode;
  action: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        marginBottom: 4,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
          {title} ({count})
        </span>
        {help}
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
        {subtotalCents !== null && (
          <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>
            Subtotal{' '}
            <span className="mono" style={{ color: 'var(--ink)' }}>
              {formatMoneyCents(subtotalCents, { precise: true })}
            </span>
          </span>
        )}
        {action}
      </span>
    </div>
  );
}
export interface InboxBlockCardProps {
  block: InboxBlock;
  pcos: SettingsPco[];
  /** Project roster defaults — prefill the Add-to-roster quick-add modal. */
  defaultStRateCents: number;
  defaultPerDiemEligible: boolean;
  /** Called after a successful save/skip/material change so the parent
   *  PDF detail + inbox list can refetch. */
  onChanged: () => Promise<void>;
  /** Daily PDF id for the View PDF button; passed through from the
   *  PdfInboxList so the modal trigger is co-located with state. */
  dailyPdfId?: string;
  /** The source PDF's report date ('YYYY-MM-DD'). Lives on the PDF, not the
   *  block, and is what tells us which billing week this block lands in — so
   *  the Submission dropdown can name the submission that already owns that
   *  week. */
  reportDate?: string | null;
  onPreviewPdf?: () => void;
  /**
   * Open the side-by-side compare viewer against the flagged sibling. The card
   * hands up the duplicate ref and the parent supplies the other half — it owns
   * the current PDF's id and filename, which the card never sees.
   */
  onComparePdfs?: (other: InboxDuplicateRef) => void;
  /** Cards render collapsed by default everywhere — a deliberate density
   *  call (commit 4b329b6); the detail pane does NOT pass this. Kept so a
   *  future surface can opt a card open (e.g. auto-expanding pending blocks). */
  defaultExpanded?: boolean;
}

export function InboxBlockCard({ block, pcos, defaultStRateCents, defaultPerDiemEligible, onChanged, dailyPdfId, reportDate = null, onPreviewPdf, onComparePdfs, defaultExpanded = false }: InboxBlockCardProps) {
  const { token, me } = useAuth();
  const toast = useToast();
  const editable = block.block_status === 'pending';
  void dailyPdfId; // captured for parity with onPreviewPdf — modal is opened by the parent state holder.
  // Near-duplicate tripwire. The server sets this only on a pending block that
  // has a non-skipped twin (same foreman + date + activity code) on another
  // PDF — the shape a re-signed/re-exported daily takes, which the byte-level
  // inbound de-dupe can't see. A WARNING, never a gate: a second report for the
  // same code on the same day is legitimate (split crews), so Save stays
  // enabled and only the PM can tell the two apart. Declared up front (rather
  // than beside the render that reads it) so handleReplace, defined earlier in
  // this component, can close over it without a use-before-declare hazard.
  const duplicateOf = block.possible_duplicate_of;
  // Tier-2 independent re-read. Non-null only when the second model's
  // transcription disagreed with THIS block's rows. Advisory: Save is untouched
  // and saving does not clear it — only the explicit "checked against the PDF"
  // on the daily header does. Absent entirely on an API older than 2026-08-03.
  const verifyFlag = block.verify_flag ?? null;
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const [laborEdits, setLaborEdits] = useState<Record<string, LaborEdit>>(() =>
    initialLaborEdits(block.labor),
  );
  const [equipmentEdits, setEquipmentEdits] = useState<Record<string, EquipmentEdit>>(() =>
    initialEquipmentEdits(block.equipment),
  );
  const [pcoId, setPcoId] = useState<string>(
    block.current_pco_id ?? block.suggested_pco_id ?? '',
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmSkip, setConfirmSkip] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [addEmployeeFor, setAddEmployeeFor] = useState<InboxLaborRow | null>(null);
  const [addEquipmentFor, setAddEquipmentFor] = useState<InboxEquipmentRow | null>(null);
  const [addLineFor, setAddLineFor] = useState<'labor' | 'equipment' | 'material' | null>(null);
  const [reassignFor, setReassignFor] = useState<ReassignTarget | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);
  // Cross-project provenance — set only when a person moved this block here
  // from another job. Server-derived (the page still names the other job).
  const transferredFrom = block.transferred_from ?? null;
  // Only a PENDING or SKIPPED block can leave for another job: a saved one is
  // in a rollup and goes out through Re-open, which is where the billed-work
  // and sent-week guards live. The server enforces the same rule (409).
  const transferable = block.block_status === 'pending' || block.block_status === 'skipped';
  const otherProjectCount = (me?.projects ?? []).filter(
    (p) => p.project_id !== me?.active_project_id,
  ).length;

  // When a roster add flips a row unknown→known (or its default rate changes),
  // re-seed the edit state so the row picks up the now-correct rate instead of
  // the stale $0 it carried while unrostered. Keyed on a structural signature
  // so ordinary refetches (e.g. a class change) don't clobber in-progress edits.
  const laborSig = block.labor
    .map((r) => `${r.daily_labor_entry_id}:${r.is_unknown}:${r.default_rate_cents}`)
    .join('|');
  const equipmentSig = block.equipment
    .map((r) => `${r.daily_equipment_entry_id}:${r.is_unknown}:${r.default_rate_cents}`)
    .join('|');
  // Merge, don't replace: preserve the PM's in-progress edits, and only pull
  // the fresh roster value into a row that (a) is brand-new, or (b) just got
  // rostered and still carries the $0 placeholder it had while unknown. This
  // fills in the rate after an Add-to-roster without clobbering edits the PM
  // made on other rows of the same block.
  useEffect(() => {
    const fresh = initialLaborEdits(block.labor);
    setLaborEdits((prev) => {
      const next = { ...prev };
      for (const r of block.labor) {
        const id = r.daily_labor_entry_id;
        const f = fresh[id];
        if (!f) continue;
        const cur = next[id];
        if (!cur) next[id] = f;
        else if (!r.is_unknown && cur.rate_cents === 0) next[id] = f;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laborSig]);
  useEffect(() => {
    const fresh = initialEquipmentEdits(block.equipment);
    setEquipmentEdits((prev) => {
      const next = { ...prev };
      for (const r of block.equipment) {
        const id = r.daily_equipment_entry_id;
        const f = fresh[id];
        if (!f) continue;
        const cur = next[id];
        if (!cur) next[id] = f;
        else if (!r.is_unknown && cur.rate_cents === 0) next[id] = f;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentSig]);

  const chosenPco = useMemo(() => pcos.find((p) => p.id === pcoId), [pcoId, pcos]);

  // Only material lines that actually bill are shown (M12). Parser candidates
  // sit in the response as 'pending' forever and are not the PM's business —
  // rendering them would be re-asking a question the client ruled unanswerable.
  const materialRows = useMemo(() => billableMaterialRows(block.materials), [block.materials]);

  // Submission routing: only surfaced when the chosen PCO has more than one
  // open submission (a split). Saves auto-pin to the default submission (R3);
  // picking a non-default routes the block there via a follow-up assign call.
  const subs = usePcoSubmissions(pcoId || null);
  const openSubmissions = useMemo(
    () => (subs.data?.submissions ?? []).filter((s) => s.status === 'open'),
    [subs.data],
  );
  const defaultSubmission = useMemo(
    () => openSubmissions.find((s) => s.is_default_target) ?? null,
    [openSubmissions],
  );
  const showSubmissionPicker = editable && openSubmissions.length > 1;
  // Holds an open submission's id. START_NEXT is a menu entry, never a stored
  // value: choosing it opens the Start Next dialog, and the submission that
  // comes back becomes the selection. Save is then an ordinary save — no
  // special flag rides along with it (dialog-first replaced the save-time
  // `start_next_submission` flag on 2026-07-29).
  const [submissionChoice, setSubmissionChoice] = useState<string>('');
  const [startNextOpen, setStartNextOpen] = useState(false);
  const submissionParentNumber = subs.data?.pco_number ?? chosenPco?.pco_number ?? '';

  // Where a plain save actually lands. The server's rule is SOLE-ROLLUP-WINS:
  // if this block's week is already collecting somewhere on the PCO, the block
  // joins that rollup's submission no matter which one is the default; the
  // default only decides brand-new weeks. Naming the default unconditionally
  // would point at the wrong package on exactly the weeks that matter. This is
  // a scan of data already in memory — every submission (any status) carries
  // its member rollups.
  const blockWeekEnding = weekEndingFor(reportDate);
  const weekOwner = useMemo(() => {
    if (!blockWeekEnding) return null;
    const owners = (subs.data?.submissions ?? []).filter((s) =>
      s.rollups.some((r) => r.week_ending_date === blockWeekEnding),
    );
    // Ambiguous (a split week across packages) — say nothing rather than
    // guess; the server picks and the PM can see the result.
    return owners.length === 1 ? (owners[0] ?? null) : null;
  }, [subs.data, blockWeekEnding]);
  // Default the picker to the block's OPEN week owner when it has one — the
  // server's sole-rollup-wins save rule means the save joins that owner
  // regardless of what the picker shows, so defaulting to anything else names
  // a destination the save won't honor (see submissionPreselect.ts). Falls
  // back to the PCO's default target otherwise: PCO switched, data loaded, a
  // new package started, the week has no owner, or its owner isn't open
  // (a closed/approved owner is the sent-week / imported-history warnings'
  // case, not this one). Deps are scalars, not the `weekOwner`/
  // `defaultSubmission` objects themselves — those are new references on
  // every poll/refetch even when nothing meaningful changed, and re-running
  // this on every refetch would stomp a PM's manual picker choice right back
  // to the preselect a few seconds later.
  useEffect(() => {
    setSubmissionChoice(pickInitialSubmissionChoice(weekOwner, defaultSubmission));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOwner?.id, weekOwner?.status, defaultSubmission?.id]);
  // Is this block's week ALREADY SENT to the GC? The owning submission's
  // rollups carry status + submitted_at, so this needs no extra fetch. When it
  // is, saving auto-reopens that week for revision (server, 2026-07-29) — the
  // PM has to know that before clicking Save, not after.
  const sentWeek = useMemo(() => {
    if (!blockWeekEnding) return null;
    const r = weekOwner?.rollups.find((x) => x.week_ending_date === blockWeekEnding) ?? null;
    if (!r) return null;
    // Billed statuses — the same set that makes a saved block `is_billed`.
    if (r.status !== 'submitted' && r.status !== 'approved' && r.status !== 'paid') return null;
    return {
      status: r.status,
      // submitted_at is a full ISO DATETIME; formatDateShort parses
      // YYYY-MM-DD. Slice, or the warning reads "on 2026-07-11T18:22:03.000Z".
      submitted_date: r.submitted_at ? r.submitted_at.slice(0, 10) : null,
      // Imported weeks CANNOT be reopened — the server refuses (409
      // imported_rollup) and routes the late block into a fresh live rollup
      // instead (the R3 escape). 32 imported weeks exist in prod, so promising
      // them a reopen would be a lie the PM acts on.
      imported: r.imported,
    };
  }, [weekOwner, blockWeekEnding]);

  const saveTarget = weekOwner ?? defaultSubmission ?? openSubmissions[0] ?? null;
  const saveTargetLabel =
    saveTarget && submissionParentNumber
      ? formatSubmissionShort(submissionParentNumber, saveTarget.submission_number)
      : null;
  // One open submission — the common case, and the one that bit us. It gets the
  // SAME dropdown as a split PCO (2026-08-05), its lone target preselected,
  // rather than a static line: the footer must not change shape from one PCO to
  // the next. It still yields entirely to the loud sent-week warning below.
  const showSoleSubmissionTarget =
    editable && !showSubmissionPicker && !!pcoId && saveTargetLabel !== null && sentWeek === null;
  const showSubmissionControl = showSubmissionPicker || showSoleSubmissionTarget;
  // A split PCO offers every open package and remembers the pick; a sole target
  // offers the one place the save actually lands (week owner first — see
  // saveTarget above), which is a readout, not a choice. Both then carry Start
  // Next, and the value is always one of the options so the trigger can never
  // fall back to an empty label.
  const submissionTargets = showSubmissionPicker
    ? openSubmissions
    : saveTarget
      ? [saveTarget]
      : [];
  const submissionValue = showSubmissionPicker ? submissionChoice : (saveTarget?.id ?? '');

  // NO MATERIAL GATE (client ruling 2026-08-03, M12). Materials never held up a
  // save for a good reason: the detector fires on narrative ("Start placing rock
  // for the area") and there is no pattern in a foreman's prose to detect. The
  // answer is "no material" unless the PM adds one by hand, so nothing about a
  // material can block Save any more.
  // Every unrostered employee + equipment line that CARRIES HOURS must be in
  // the roster before save — an unrostered line has no rate, so its hours would
  // silently bill $0. A 0-hour unrostered line can't misbill (foremen list the
  // whole crew under a code and zero out whoever didn't work it), so it saves
  // as-is and keeps its "unknown" badge. Counts the hours actually being
  // committed — the live edit state — so zeroing a row clears the block and
  // adding hours back restores it. Server enforces the same gate on the same
  // payload.
  const unrosteredCount =
    block.labor.filter((r) => {
      if (!r.is_unknown) return false;
      const e = laborEdits[r.daily_labor_entry_id];
      return (e ? e.total_hours : r.confirmed_total_hours ?? r.parsed_total_hours) > 0;
    }).length +
    block.equipment.filter((r) => {
      if (!r.is_unknown) return false;
      const e = equipmentEdits[r.daily_equipment_entry_id];
      return (e ? e.hours : r.confirmed_hours ?? r.parsed_hours) > 0;
    }).length;
  // Lines with worked hours but a $0 rate would bill $0. A 0-hour line is fine
  // (bills nothing by design). Mirrors the server's zero_rate_with_hours guard.
  const zeroRateWithHours =
    block.labor.filter((r) => {
      const e = laborEdits[r.daily_labor_entry_id];
      return !!e && e.total_hours > 0 && e.rate_cents === 0;
    }).length +
    block.equipment.filter((r) => {
      const e = equipmentEdits[r.daily_equipment_entry_id];
      return !!e && e.hours > 0 && e.rate_cents === 0;
    }).length;
  const saveBlockedReason = !pcoId
    ? 'Pick a PCO first'
    : unrosteredCount > 0
      ? `Add ${unrosteredCount} unrostered ${unrosteredCount === 1 ? 'entry' : 'entries'} with hours to the roster first — otherwise ${unrosteredCount === 1 ? 'it bills' : 'they bill'} at $0`
      : zeroRateWithHours > 0
        ? `${zeroRateWithHours} line${zeroRateWithHours === 1 ? '' : 's'} have hours but a $0 rate — set a rate or it bills $0`
        : null;

  /**
   * Labor/equipment/pco_id payload shared by an ordinary save and Replace
   * older block — replace posts this exact shape, plus which saved block it
   * retires.
   */
  const buildSavePayload = () => ({
    pco_id: pcoId,
    labor: Object.entries(laborEdits).map(([id, e]) => ({
      daily_labor_entry_id: id,
      total_hours: e.total_hours,
      rate_cents: e.rate_cents,
      daypart: e.daypart,
    })),
    equipment: Object.entries(equipmentEdits).map(([id, e]) => ({
      daily_equipment_entry_id: id,
      hours: e.hours,
      rate_cents: e.rate_cents,
    })),
  });

  const handleSave = async () => {
    if (!token) return;
    if (!pcoId) {
      setErr('Pick a PCO first.');
      return;
    }
    setBusy(true);
    setErr(null);
    // A save that landed but couldn't be routed to the chosen submission is a
    // partial success: it must stay open so the PM can read where the block
    // actually went. Only a clean save collapses.
    let assignFailed = false;
    try {
      await api.inbox.saveBlock(token, block.daily_entry_id, buildSavePayload());
      // Save auto-pins to the default submission (R3). When the PM picked a
      // non-default open submission, route the saved block there. The save is
      // already committed at this point, so an assign failure must not read as
      // "nothing happened" — the success path below still runs, and the error
      // says exactly where the block actually landed.
      // Exception: when the pick IS the week's owner (the preselect's common
      // case), the save already landed there via sole-rollup-wins — an assign
      // would only spin up a redundant default-side rollup and leave the week
      // pointlessly pinned.
      if (
        showSubmissionPicker &&
        submissionChoice &&
        submissionChoice !== defaultSubmission?.id &&
        submissionChoice !== weekOwner?.id
      ) {
        try {
          await api.blocks.assignSubmission(token, submissionChoice, [block.daily_entry_id]);
        } catch (assignFailure) {
          assignFailed = true;
          const chosen = openSubmissions.find((s) => s.id === submissionChoice);
          setErr(
            `Saved to the default submission, but couldn't route it to ${formatSubmissionLabel(
              submissionParentNumber,
              chosen?.submission_number ?? null,
            )}: ${submissionErrorMessage(assignFailure, 'the assignment failed.', gcName(me))} Use "Transfer Block" on the rollup to finish.`,
          );
        }
      }
      await onChanged();
      // The PCO's submission set just changed — a new package may be open and
      // the default may have moved. Every other block card on this PCO reads
      // the same query, so refreshing it here is what keeps their Submission
      // dropdowns from showing a default that no longer exists.
      invalidatePcoSubmissions(pcoId);
      if (chosenPco) {
        toast.push({
          variant: 'success',
          title: 'Saved',
          body: `Saved to PCO ${formatPcoNumber(chosenPco.pco_number)} — ${chosenPco.description}`,
        });
      }
      // COLLAPSE ON SUCCESS (client, 2026-08-03; supersedes the earlier
      // stay-expanded direction). A decided block is finished work — leaving it
      // open pushed the next PENDING block a screen down, and the PM's next
      // move is always that block, never this one. Runs after the refetch so
      // the header the card collapses to is already carrying the Saved pill,
      // and after the toast so the confirmation is on screen either way. A
      // failed save never reaches here and keeps its error visible.
      if (!assignFailed) setExpanded(false);
    } catch (e) {
      // No `week_already_collecting` branch any more: this is an ordinary
      // save that never asks the server to open a package, so that 409 has
      // nothing to fire on. Starting a package is the dialog's job now.
      if (e instanceof ApiCallError) {
        const p = e.payload as { error?: string; message?: string };
        setErr(p.message ?? p.error ?? 'Save failed');
      } else {
        setErr(e instanceof Error ? e.message : 'Save failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSkip = async () => {
    if (!token) return;
    setBusy(true);
    setErr(null);
    try {
      await api.inbox.skipBlock(token, block.daily_entry_id);
      await onChanged();
      // Same rule as save: a decided block collapses to its header. Skip has
      // never behaved differently on purpose — it just had no collapse either.
      setExpanded(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Skip failed');
    } finally {
      setBusy(false);
    }
  };

  /**
   * "Replace older block" — the near-duplicate banner's one-click
   * resolution for the case where the sibling is already SAVED. Saves this
   * block through the same gates an ordinary save runs, then retires the
   * older one server-side. `duplicateOf.daily_entry_id` is optional on the
   * wire (older API responses won't carry it) — the button that triggers
   * this is hidden whenever it's absent, so `targetId` should always be set
   * here, but the guard stays cheap insurance against a stale closure.
   */
  const handleReplace = async () => {
    const targetId = duplicateOf?.daily_entry_id;
    if (!token || !targetId) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.inbox.replaceBlock(token, block.daily_entry_id, {
        ...buildSavePayload(),
        replaces_daily_entry_id: targetId,
      });
      await onChanged();
      invalidatePcoSubmissions(pcoId);
      if (chosenPco) {
        toast.push({
          variant: 'success',
          title: 'Saved',
          body: res.partial
            ? `Saved to PCO ${formatPcoNumber(chosenPco.pco_number)} — ${chosenPco.description}.`
            : `Saved to PCO ${formatPcoNumber(chosenPco.pco_number)} — ${chosenPco.description}. Retired the older block on ${duplicateOf?.filename ?? 'the other PDF'}.`,
        });
      }
      // A half-done replace must never read as a clean success — the save
      // committed, but the old block still needs the PM's hand.
      if (res.partial && res.remaining_action) {
        toast.push({
          variant: 'error',
          title: 'Finish the replace by hand',
          body: res.remaining_action,
        });
      }
      // Replace IS a save, so it collapses like one — but only when it fully
      // succeeded. A partial replace still has a manual step attached to it and
      // stays open.
      if (!res.partial) setExpanded(false);
    } catch (e) {
      if (e instanceof ApiCallError) {
        const p = e.payload as { error?: string; message?: string };
        setErr(p.message ?? p.error ?? 'Replace failed');
      } else {
        setErr(e instanceof Error ? e.message : 'Replace failed');
      }
    } finally {
      setBusy(false);
      setConfirmReplace(false);
    }
  };

  const handleReopen = async () => {
    if (!token) return;
    setBusy(true);
    setErr(null);
    try {
      await api.inbox.reopenBlock(token, block.daily_entry_id);
      await onChanged();
    } catch (e) {
      // Prefer the server's friendly `message` (e.g. the block_is_billed
      // explanation) over the bare error code.
      if (
        e instanceof ApiCallError &&
        e.payload &&
        typeof e.payload === 'object' &&
        'message' in e.payload &&
        typeof (e.payload as { message?: unknown }).message === 'string'
      ) {
        setErr((e.payload as { message: string }).message);
      } else {
        setErr(e instanceof Error ? e.message : 'Re-open failed');
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * After a reassign, reprice THAT row's in-progress edit from the fresh
   * roster link. Hours stay as the PM had them (identity-independent). The
   * shift re-derives from the NEW identity's primary daypart — matching what
   * Add-to-roster seeding would do — UNLESS the PM actually changed the shift
   * away from the row's seeded value (a real edit is kept; the seeded
   * placeholder default is not an edit). The rate then follows the chosen
   * shift at the new roster identity's rates — $0 when the line was reset to
   * the PDF identity (unknown lines price at $0 until linked).
   */
  const applyReassignReprice = (target: ReassignTarget, fresh: InboxBlock) => {
    if (target.section === 'labor') {
      const freshRow = fresh.labor.find(
        (r) => r.daily_labor_entry_id === target.row.daily_labor_entry_id,
      );
      if (!freshRow) return;
      // What initialLaborEdits seeded for the OLD row — the baseline for
      // "did the PM actually touch the shift?".
      const seededDaypart =
        target.row.confirmed_total_hours !== null
          ? target.row.daypart
          : target.row.primary_daypart;
      setLaborEdits((prev) => {
        const cur = prev[freshRow.daily_labor_entry_id];
        const pmChangedDaypart = cur !== undefined && cur.daypart !== seededDaypart;
        const daypart = pmChangedDaypart ? cur.daypart : freshRow.primary_daypart;
        return {
          ...prev,
          [freshRow.daily_labor_entry_id]: {
            total_hours:
              cur?.total_hours ?? freshRow.confirmed_total_hours ?? freshRow.parsed_total_hours,
            rate_cents: rateForDaypart(freshRow, daypart),
            daypart,
          },
        };
      });
    } else {
      const freshRow = fresh.equipment.find(
        (r) => r.daily_equipment_entry_id === target.row.daily_equipment_entry_id,
      );
      if (!freshRow) return;
      setEquipmentEdits((prev) => {
        const cur = prev[freshRow.daily_equipment_entry_id];
        return {
          ...prev,
          [freshRow.daily_equipment_entry_id]: {
            hours: cur?.hours ?? freshRow.confirmed_hours ?? freshRow.parsed_hours,
            rate_cents: freshRow.default_rate_cents,
          },
        };
      });
    }
  };

  const savedPco = block.current_pco_id
    ? pcos.find((p) => p.id === block.current_pco_id) ?? null
    : null;

  return (
    <div
      style={{
        borderRadius: 4,
        background: '#fff',
        // OUTLINE ON EXPANDED, STATUS BAR ON COLLAPSED. The two cues fight —
        // a 3px colored left edge under a blue selected-border reads as two
        // competing frames on one box — so each owns the state it is actually
        // for. The bar is the scan cue down a stack of collapsed rows, where
        // status is the only question; the outline says "this is the one you
        // have open", which a collapsed row never needs to say. An expanded
        // card still shows its status on its own chip.
        ...(expanded
          ? {
              border: '1px solid var(--accent)',
              boxShadow: '0 0 0 3px var(--accent-tint-10)',
            }
          : {
              border: '1px solid var(--rule)',
              borderLeft: `3px solid ${blockStatusColor(block.block_status)}`,
            }),
      }}
    >
      {/* Header — always visible, click to expand/collapse */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: '100%',
          padding: '12px 16px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
          color: 'inherit',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <span className="mono" style={{ fontSize: 13, color: 'var(--ink-2)' }}>
            {block.activity_code}
          </span>
          {block.code_description && (
            <span style={{ fontSize: 14, color: 'var(--ink)' }}>{block.code_description}</span>
          )}
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 14 }}>
          {duplicateOf && (
            <span
              className="status-chip filled s-danger"
              title={`Possible duplicate — a block with the same foreman, date, and code exists on ${duplicateOf.filename}.`}
            >
              Possible duplicate
            </span>
          )}
          {/* Cards render collapsed by default, so the strip inside the body
              would be invisible to a PM scanning the pane. The chip is how the
              collapsed card says "the disagreement is in here". */}
          {verifyFlag && (
            <span className="status-chip s-warning" title={verifyFlag.headline}>
              Second read disagrees
            </span>
          )}
          {onPreviewPdf && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPreviewPdf();
              }}
              className="btn-link"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 12.5,
                color: 'var(--ink-3)',
                border: 'none',
              }}
              title="View original PDF"
            >
              <FileText size={14} aria-hidden />
              View PDF
            </button>
          )}
          <BlockStatusPill status={block.block_status} />
          <BlockChevron expanded={expanded} />
        </span>
      </button>

      {/* Body — rendered only when expanded */}
      {expanded && (
        <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* CROSS-PROJECT PROVENANCE. A muted note, not a warning: the block is
              where a person deliberately put it. What it has to answer is "why
              does this page say a different job than the one I'm standing in",
              which is otherwise unanswerable from this screen. */}
          {transferredFrom && (
            <div className="muted" style={{ fontSize: 12.5 }}>
              Transferred from job {transferredFrom.printed_job_number}
              {transferredFrom.filed_activity_code
                ? ` — filed as ${transferredFrom.filed_activity_code}`
                : ''}
              .
            </div>
          )}
          {/* The independent re-read's disagreement, on the block that carries
              it. Sits ABOVE the tables it is about — the whole instruction is
              "check these rows against the PDF", and it has to be read before
              the rows, not after them. Amber, not red: it never blocks a save
              and it is wrong often enough that red would be a lie (the
              near-duplicate banner below is the only red in this card, because
              missing that one double-bills the GC). */}
          {verifyFlag && (
            <div className="inbox-block-verify">
              <AlertTriangle size={14} aria-hidden style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div className="inbox-block-verify-head">{verifyFlag.headline}</div>
                {verifyFlag.rows.length > 0 && (
                  <ul className="inbox-block-verify-rows">
                    {verifyFlag.rows.map((r, i) => (
                      <li key={`${r.section}-${r.label}-${i}`}>
                        <span className="inbox-block-verify-where">
                          {r.section === 'labor' ? 'Labor' : 'Equipment'}
                        </span>
                        {r.label}
                        {' — '}
                        {[r.stored_summary, r.read_summary].filter(Boolean).join(', ') ||
                          'nothing to compare'}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          <section>
        <SectionHeader
          title="Labor"
          count={block.labor.length}
          subtotalCents={laborSubtotalCents(block.labor, laborEdits)}
          help={
            <HelpTip title="Edits stay on this block" learnMore="inbox">
              Rate or hours edits here apply only to this block — they won't change the
              employee's default rate in Project Settings. The + Add labor button adds a
              crew member the foreman's PDF didn't include; it attaches to this block and
              bills like any other line, tagged as manually added.
            </HelpTip>
          }
          action={
            editable && (
              <button
                type="button"
                className="btn btn-ghost btn-accent"
                style={addLineButtonStyle}
                onClick={() => setAddLineFor('labor')}
                title="Add a labor line the foreman's PDF didn't include — e.g. a crew member the report missed. It attaches to this block and bills like any other line."
              >
                + Add labor
              </button>
            )
          }
        />
        <InboxLaborTable
          rows={block.labor}
          edits={laborEdits}
          editable={editable}
          onChange={(id, e) => setLaborEdits((prev) => ({ ...prev, [id]: e }))}
          onClassChanged={onChanged}
          onAddToRoster={setAddEmployeeFor}
          onReassign={(row) => setReassignFor({ section: 'labor', row })}
          onRemoveManual={async (row) => {
            if (!token) return;
            try {
              await api.inbox.removeManualLine(token, block.daily_entry_id, 'labor', row.daily_labor_entry_id);
              await onChanged();
            } catch (e) { setErr(e instanceof Error ? e.message : 'Remove failed'); }
          }}
        />
      </section>

      <section>
        <SectionHeader
          title="Equipment"
          count={block.equipment.length}
          subtotalCents={equipmentSubtotalCents(block.equipment, equipmentEdits)}
          help={
            <HelpTip title="Edits stay on this block" learnMore="inbox">
              Rate or hours edits here apply only to this block — they won't change the
              equipment's default rate in Project Settings. The + Add equipment button adds a
              machine that ran but wasn't on the PDF; it attaches to this block and bills
              like any other line, tagged as manually added.
            </HelpTip>
          }
          action={
            editable && (
              <button
                type="button"
                className="btn btn-ghost btn-accent"
                style={addLineButtonStyle}
                onClick={() => setAddLineFor('equipment')}
                title="Add an equipment line the foreman's PDF didn't include — e.g. a machine that ran but wasn't logged. It attaches to this block and bills like any other line."
              >
                + Add equipment
              </button>
            )
          }
        />
        <InboxEquipmentTable
          rows={block.equipment}
          edits={equipmentEdits}
          editable={editable}
          onChange={(id, e) => setEquipmentEdits((prev) => ({ ...prev, [id]: e }))}
          onAddEquipment={setAddEquipmentFor}
          onReassign={(row) => setReassignFor({ section: 'equipment', row })}
          onRemoveManual={async (row) => {
            if (!token) return;
            try {
              await api.inbox.removeManualLine(token, block.daily_entry_id, 'equipment', row.daily_equipment_entry_id);
              await onChanged();
            } catch (e) { setErr(e instanceof Error ? e.message : 'Remove failed'); }
          }}
        />
      </section>

      {block.notes && block.notes.trim() !== '' && (
        /* pre-wrap, and the label on its own line above the text: the stored
           notes keep the foreman's line breaks, and some of them write in
           sections — a standalone Equipment heading, one machine per line,
           then Export / Import. Flattened onto one wrapped paragraph beside an
           inline label, that structure is unreadable. pre-wrap (not pre-line)
           so space-indented sub-lines survive too — the detail modal and the
           regenerated daily both keep them, and the card must agree. */
        <div
          style={{
            fontSize: 13,
            color: 'var(--ink-2)',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 6 }}>Notes</div>
          {block.notes}
        </div>
      )}

      {(materialRows.length > 0 || editable) && (
        <section>
          <SectionHeader
            title="Materials"
            count={materialRows.length}
            /* Only priced when lines exist — an empty materials section shows
               no subtotal rather than a $0.00 the PM might read as a total
               someone computed. */
            subtotalCents={
              materialRows.length > 0 ? materialsSubtotalCents(materialRows) : null
            }
            help={
              <HelpTip title="Materials are added by hand" learnMore="materials">
                A foreman&rsquo;s report never carries a materials table, so the portal never
                proposes one. If a material belongs on this block, click + Add material and
                enter it — it attaches to this block and bills like any other line, tagged as
                manually added. A block with no material lines bills no materials.
              </HelpTip>
            }
            action={
              editable && (
                <button
                  type="button"
                  className="btn btn-ghost btn-accent"
                  style={addLineButtonStyle}
                  onClick={() => setAddLineFor('material')}
                  title="Add a material line the foreman's PDF didn't include. It attaches to this block and bills like any other line."
                >
                  + Add material
                </button>
              )
            }
          />
          <InboxMaterialsList
            rows={materialRows}
            editable={editable}
            onRemoveManual={async (row) => {
              if (!token) return;
              try {
                await api.inbox.removeManualLine(token, block.daily_entry_id, 'material', row.material_queue_item_id);
                await onChanged();
              } catch (e) { setErr(e instanceof Error ? e.message : 'Remove failed'); }
            }}
          />
        </section>
      )}

      {editable && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px', borderRadius: 6, background: 'var(--accent-tint-10)', border: '1px solid #ccd5f0', borderLeft: '3px solid var(--accent)' }}>
          {/* Sits inside the save bar on purpose — it has to be read BEFORE the
              Save click, not scrolled past at the top of the card. */}
          {duplicateOf && (
            // Solid red, white text (client, 2026-07-29). Every other warning in
            // this save bar is amber; this one is the only one where missing it
            // bills the GC for the same day twice, so it deliberately does not
            // blend in with them.
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 6,
                border: '1px solid var(--danger)',
                background: 'var(--danger)',
                fontSize: 12.5,
                lineHeight: 1.45,
                color: '#ffffff',
              }}
            >
              <AlertTriangle
                size={14}
                aria-hidden
                style={{ color: '#ffffff', flexShrink: 0, marginTop: 2 }}
              />
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '6px 16px',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <span style={{ flex: '1 1 220px' }}>
                  <strong style={{ color: '#ffffff', fontWeight: 700 }}>
                    Possible duplicate
                  </strong>
                  {' — a block with the same foreman, date, and code exists on '}
                  <span style={{ color: '#ffffff', fontWeight: 600 }}>{duplicateOf.filename}</span>
                  {` (${duplicateOf.block_status === 'saved' ? 'saved' : 'pending'}).`}
                  <HelpTip title="Checking a possible duplicate" learnMore="inbox" inverse>
                    A re-sent or re-signed copy of a report has different file contents, so the
                    automatic duplicate check doesn&rsquo;t catch it — saving both would bill the
                    day twice. Two genuinely separate reports for the same code on the same day
                    are also normal (split crews). Check the other report before saving; this
                    doesn&rsquo;t block anything. &ldquo;Compare PDFs&rdquo; opens both documents
                    side by side so you can settle it without leaving the block.
                  </HelpTip>
                </span>
                {/* Actions pinned to the banner's far right, with real gap between
                    them (Brandon, 2026-07-30: Compare was underlined link text
                    sitting almost against Replace — the two read as one smudged
                    control). Both stay white-on-red rather than reaching for a
                    third color: the banner's red is load-bearing (a missed
                    duplicate double-bills the GC) and neither button may compete
                    with it for attention. Compare is the lower-commitment action
                    (open, look, decide) so it takes the quieter outline; Replace
                    is the one-click resolution so it keeps the solid fill. */}
                {((onComparePdfs && duplicateOf.daily_pdf_id) ||
                  (editable && duplicateOf.block_status === 'saved' && duplicateOf.daily_entry_id)) && (
                  <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
                    {/* The decision this banner asks for is "are these the same day's
                        work?", and that is unanswerable from one document. Opens both
                        at once rather than toggling one viewer — toggling meant
                        scroll, click, scroll back, click, and holding the first
                        report in your head. */}
                    {onComparePdfs && duplicateOf.daily_pdf_id && (
                      <button
                        type="button"
                        onClick={() => onComparePdfs(duplicateOf)}
                        style={{
                          fontSize: 12.5,
                          fontWeight: 600,
                          padding: '3px 10px',
                          borderRadius: 4,
                          border: '1px solid #ffffff',
                          background: 'transparent',
                          color: '#ffffff',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Compare PDFs
                      </button>
                    )}
                    {/* Only the SAVED-sibling case is worth a guided action — a
                        pending twin needs no ceremony, the PM just skips one (see
                        the help copy above). Hidden whenever the API hasn't sent
                        a sibling block id yet (older response shape) — there is
                        nothing to address without it, and this must never guess
                        one. Solid white-on-red so it reads as danger-adjacent
                        against the banner, distinct from the outlined Compare
                        button. */}
                    {editable && duplicateOf.block_status === 'saved' && duplicateOf.daily_entry_id && (
                      <button
                        type="button"
                        onClick={() => setConfirmReplace(true)}
                        style={{
                          fontSize: 12.5,
                          fontWeight: 700,
                          padding: '3px 10px',
                          borderRadius: 4,
                          border: 'none',
                          background: '#ffffff',
                          color: 'var(--danger)',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Replace older block
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Late PDF for a week the GC already has. Same amber treatment and
              same in-the-save-bar placement as the near-duplicate warning
              above: it changes what Save DOES, so it has to be read first. */}
          {editable && sentWeek && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 6,
                border: '1px solid var(--warning)',
                background: 'var(--warning-tint)',
                fontSize: 12.5,
                lineHeight: 1.45,
                color: 'var(--ink)',
              }}
            >
              <AlertTriangle
                size={14}
                aria-hidden
                style={{ color: 'var(--warning)', flexShrink: 0, marginTop: 2 }}
              />
              {sentWeek.imported ? (
                <span>
                  <strong style={{ color: 'var(--warning)', fontWeight: 600 }}>
                    This week is imported history
                  </strong>
                  {saveTargetLabel ? ` (${saveTargetLabel})` : ''}
                  {'. If this work is genuinely new — '}
                  {gcName(me)}
                  {' never billed it — Save: it bills as its own fresh week under the current '}
                  {'submission. If '}
                  {gcName(me)}
                  {'’s side already re-billed this week’s revision, Skip this block '}
                  {'instead — saving it here would bill the work twice.'}
                  <HelpTip title="Imported weeks and late work" learnMore="imported-history">
                    An imported week is a copy of the original spreadsheet from before the
                    portal — it has no line items to rebuild, so it can never be reopened or
                    resent. Late work for that week bills through the current open submission
                    instead, as its own week — unless {gcName(me)}&rsquo;s own revision already
                    includes it, in which case Skip.
                  </HelpTip>
                </span>
              ) : (
                <span>
                  <strong style={{ color: 'var(--warning)', fontWeight: 600 }}>
                    This week was already sent
                  </strong>
                  {saveTargetLabel ? ` as ${saveTargetLabel}` : ''}
                  {sentWeek.submitted_date
                    ? ` on ${formatDateShort(sentWeek.submitted_date)}`
                    : ''}
                  {'. Saving reopens it for revision — you\u2019ll need to resend the week so '}
                  {gcName(me)}
                  {' gets the added rows.'}
                  <HelpTip title="Reopening an already-sent week" learnMore="weekly-rollups">
                    Late reports are normal. The week goes back to Active with this block folded
                    in, and its money leaves Submitted-to-date until you resend — that dip is
                    real, not a bug. The resend is marked REVISED and the spreadsheet highlights
                    the added rows so their reviewer only re-checks the delta.
                  </HelpTip>
                </span>
              )}
            </div>
          )}
          {/* The footer names itself, in the same voice as the data sections
              above — the bar reads as "the decision" rather than a strip of
              loose controls under the tables. */}
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 6 }}>
            Assign this block
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="eyebrow" style={{ color: 'var(--ink)', fontSize: 13 }}>PCO</span>
            <PcoAssignmentDropdown
              pcos={pcos}
              value={pcoId}
              onChange={setPcoId}
              disabled={busy}
            />
            {showSubmissionControl && (
              <>
                <span className="eyebrow" style={{ color: 'var(--ink)', fontSize: 13 }}>
                  Submission
                </span>
                <Dropdown
                  value={submissionValue}
                  aria-label="Submission"
                  options={[
                    ...submissionTargets.map((s) => ({
                      value: s.id,
                      label: formatSubmissionLabel(submissionParentNumber, s.submission_number),
                    })),
                    // Always last, always offered, in both shapes. It doesn't
                    // route this block — it opens a package. Where the block
                    // lands stays the separate question the selection above
                    // answers honestly: a week that already lives somewhere
                    // joins its owner no matter what was just started. The
                    // dialog does the work and the new submission comes back
                    // selected.
                    { value: START_NEXT, label: 'Start Next…' },
                  ]}
                  onChange={(next) => {
                    // START_NEXT is a menu entry, not a value — it opens the
                    // dialog and leaves the current selection alone until a
                    // package actually comes back.
                    if (next === START_NEXT) setStartNextOpen(true);
                    else setSubmissionChoice(next);
                  }}
                  disabled={busy}
                />
                {/* Explains why the picker landed where it did when the
                    preselect (above) picked the week owner over the default —
                    otherwise the choice looks arbitrary. Only a split PCO can
                    have picked one over the other, so only a split PCO says it. */}
                {showSubmissionPicker &&
                  weekOwner?.status === 'open' &&
                  submissionChoice === weekOwner.id &&
                  blockWeekEnding && (
                    <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                      matches this block&rsquo;s week (WE {formatDateShort(blockWeekEnding)})
                    </span>
                  )}
              </>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              {/* Hidden entirely on a single-job account: there is nowhere to
                  move to, so the button would only ever be a dead end. */}
              {otherProjectCount > 0 && (
                <button
                  type="button"
                  className="btn-link"
                  onClick={() => setTransferOpen(true)}
                  disabled={busy}
                  title="This work is billing on another job — move the block there and re-price it against that job's roster."
                >
                  Move to another job
                </button>
              )}
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setConfirmSkip(true)}
                disabled={busy}
                style={{ background: '#ffffff', fontWeight: 700 }}
              >
                Skip block
              </button>
              <button
                type="button"
                className="btn btn-cta"
                onClick={handleSave}
                disabled={busy || saveBlockedReason !== null}
                title={saveBlockedReason ?? undefined}
                style={{
                  fontWeight: 700,
                  ...(saveBlockedReason !== null
                    ? { opacity: 0.5, cursor: 'not-allowed' }
                    : {}),
                }}
              >
                {busy ? 'Saving…' : 'Save to PCO'}
              </button>
            </div>
          </div>
          {err && <span style={{ color: 'var(--danger)', fontSize: 12 }}>{err}</span>}
          {!err && saveBlockedReason && (
            <span style={{ color: 'var(--ink-2)', fontSize: 12 }}>{saveBlockedReason}</span>
          )}
        </div>
      )}

      <ConfirmModal
        open={confirmSkip}
        title="Skip this block?"
        body={
          <>
            Skipping drops this block from the rollup — its labor, equipment, and
            materials won&rsquo;t be billed. You can re-open it later if you change
            your mind.
          </>
        }
        confirmLabel="Skip block"
        cancelLabel="Cancel"
        destructive
        busy={busy}
        busyLabel="Skipping…"
        onConfirm={async () => {
          await handleSkip();
          setConfirmSkip(false);
        }}
        onCancel={() => setConfirmSkip(false)}
      />

      <ConfirmModal
        open={confirmReplace}
        title="Replace the older block?"
        body={
          <>
            Saves this block and retires the older one from billing. If the week was already
            sent it reopens for a REVISED resend.
          </>
        }
        confirmLabel="Replace older block"
        cancelLabel="Cancel"
        busy={busy}
        busyLabel="Replacing…"
        onConfirm={handleReplace}
        onCancel={() => setConfirmReplace(false)}
      />

      <AddEmployeeModal
        open={addEmployeeFor !== null}
        initialHjId={addEmployeeFor?.hj_id ?? ''}
        initialName={addEmployeeFor?.name ?? ''}
        defaultStRateCents={defaultStRateCents}
        defaultPerDiemEligible={defaultPerDiemEligible}
        onClose={() => setAddEmployeeFor(null)}
        onAdded={async () => {
          setAddEmployeeFor(null);
          await onChanged();
        }}
      />

      <AddEquipmentModal
        open={addEquipmentFor !== null}
        initialAssetId={addEquipmentFor?.asset_id ?? ''}
        initialDescription={addEquipmentFor?.description ?? ''}
        onClose={() => setAddEquipmentFor(null)}
        onAdded={async () => {
          setAddEquipmentFor(null);
          await onChanged();
        }}
      />

      {addLineFor && (
        <InboxAddLineModal
          section={addLineFor}
          dailyEntryId={block.daily_entry_id}
          defaultStRateCents={defaultStRateCents}
          defaultPerDiemEligible={defaultPerDiemEligible}
          onClose={() => setAddLineFor(null)}
          onAdded={async () => { await onChanged(); }}
        />
      )}

      {reassignFor && (
        <InboxReassignModal
          target={reassignFor}
          dailyEntryId={block.daily_entry_id}
          foreman={block.foreman ?? null}
          onClose={() => setReassignFor(null)}
          onReassigned={async (freshBlock) => {
            applyReassignReprice(reassignFor, freshBlock);
            await onChanged();
          }}
        />
      )}

      {/* Cross-project transfer. On success the block has LEFT this project, so
          the toast is the only place its outcome can be reported — the card is
          about to disappear from this list. It names the unknown rows because
          that is the one thing waiting for the PM on the other side. */}
      <TransferProjectModal
        open={transferOpen && transferable}
        block={block}
        onCancel={() => setTransferOpen(false)}
        onTransferred={async ({ projectLabel, resolution }) => {
          setTransferOpen(false);
          const unknown = resolution.labor_unknown + resolution.equipment_unknown;
          toast.push({
            variant: unknown > 0 ? 'warning' : 'success',
            title: `Moved to job ${projectLabel}`,
            body:
              unknown > 0
                ? `${unknown} ${unknown === 1 ? 'line' : 'lines'} didn't match that job's roster and show as unknown — add them there before you can save the block.`
                : `Everyone and everything matched that job's roster. The block is waiting in job ${projectLabel}'s PDF Inbox.`,
          });
          await onChanged();
        }}
      />

      {/* Same dialog the PCO page's "Start Next" button opens. It only opens a
          package; this block still saves by the ordinary rules, into whatever
          the Submission dropdown above names. Its reference source comes from
          the FULL submission list — deliberately not from whatever this block
          happens to target. */}
      {startNextOpen && pcoId && (
        <StartNextSubmissionDialog
          pcoId={pcoId}
          pcoParentNumber={submissionParentNumber || (chosenPco?.pco_number ?? '')}
          submissions={subs.data?.submissions ?? []}
          onClose={() => setStartNextOpen(false)}
          onDone={async (opened) => {
            // Select what was just opened so the next save goes there without
            // a second decision; every other card on this PCO refreshes too.
            if (opened) setSubmissionChoice(opened.id);
            invalidatePcoSubmissions(pcoId);
            await subs.refetch();
          }}
        />
      )}

      {block.block_status === 'saved' && (
        <div
          style={{
            fontSize: 13,
            color: 'var(--ink-3)',
            borderTop: '1px solid var(--rule)',
            paddingTop: 8,
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <span>
            Saved
            {savedPco && (
              <>
                {' to '}
                <span style={{ color: 'var(--ink-2)' }}>
                  PCO {formatPcoNumber(savedPco.pco_number)} — {savedPco.description}
                </span>
              </>
            )}
            .
          </span>
          {err && <span style={{ color: 'var(--danger)', fontSize: 12 }}>{err}</span>}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleReopen}
            disabled={busy || block.is_billed}
            style={block.is_billed ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            title={
              block.is_billed
                ? 'This block has been billed — reassignment with corrective resubmit coming post-trial. Contact support to correct.'
                : 'Flip back to pending so you can edit and re-save.'
            }
          >
            {busy ? 'Re-opening…' : 'Re-open'}
          </button>
        </div>
      )}
      {block.block_status === 'skipped' && (
        <div
          style={{
            fontSize: 13,
            color: 'var(--ink-3)',
            borderTop: '1px solid var(--rule)',
            paddingTop: 8,
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <span>Skipped.</span>
          {err && <span style={{ color: 'var(--danger)', fontSize: 12 }}>{err}</span>}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleReopen}
            disabled={busy}
            title="Flip back to pending so you can review this block again."
          >
            {busy ? 'Re-opening…' : 'Re-open'}
          </button>
          {/* A skipped block is the OTHER shape this feature exists for: work
              set aside on the wrong job. It transfers straight across and lands
              pending on the target, no reopen step in between. */}
          {otherProjectCount > 0 && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setTransferOpen(true)}
              disabled={busy}
              title="This work is billing on another job — move the block there and re-price it against that job's roster."
            >
              Move to another job
            </button>
          )}
        </div>
      )}
      {/* IGNORED: no Re-open button, deliberately. 'ignored' is the CODE's
          state projected onto its blocks, so reversing it per-block would
          contradict itself the moment the next PDF arrives — the server
          refuses it too (409 block_ignored). The way back is un-ignoring the
          code, which restores every block under it at once. */}
      {block.block_status === 'ignored' && (
        <div
          style={{
            fontSize: 13,
            color: 'var(--ink-3)',
            borderTop: '1px solid var(--rule)',
            paddingTop: 8,
          }}
        >
          This activity code is on the ignore list — it never bills through the portal, so
          this block isn&rsquo;t work waiting for you. To bring it back, un-ignore the code
          in Settings &rarr; PCOs &rarr; &ldquo;Ignored codes&rdquo;.
        </div>
      )}
        </div>
      )}
    </div>
  );
}

/** Left-edge bar color so block status reads at a glance even when several
 *  blocks share the same pill — pending/saved/skipped match the status-chip
 *  tokens (warning/positive/ink-4). 'ignored' shares the muted 'skipped' bar:
 *  both mean "decided, bills nothing", and the pill names which is which. */
function blockStatusColor(status: InboxBlock['block_status']): string {
  return status === 'saved'
    ? 'var(--positive)'
    : status === 'skipped' || status === 'ignored'
      ? 'var(--ink-4)'
      : 'var(--warning)';
}

const BLOCK_STATUS_LABELS: Record<InboxBlock['block_status'], string> = {
  pending: 'Pending',
  saved: 'Saved',
  skipped: 'Skipped',
  ignored: 'Ignored',
};

function BlockStatusPill({ status }: { status: InboxBlock['block_status'] }) {
  const label = BLOCK_STATUS_LABELS[status];
  // Quiet bordered chip — dot + small-caps label. The old filled variant
  // read as SaaS slop next to neutral inbox rows; the restrained dot
  // pattern reads as "metadata about the row," not "shouting at the user."
  return <span className={`status-chip s-${status}`}>{label}</span>;
}

/**
 * Disclosure chevron for the block card header. Inline SVG so the icon
 * reads at any size; rotates 180° when the block is expanded.
 */
function BlockChevron({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      style={{
        flexShrink: 0,
        color: 'var(--ink-3)',
        transition: 'transform 150ms ease',
        transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
      }}
    >
      <path
        d="M4 6 L8 10 L12 6"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
